import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    BarChart,
    FilterList,
    KeyboardArrowDown,
    PictureAsPdf,
    TableView,
    ViewModule,
    ViewWeek,
} from '@mui/icons-material';
import {
    Box,
    Badge,
    Card,
    CardContent,
    Chip,
    Collapse,
    FormControl,
    IconButton,
    InputLabel,
    Menu,
    MenuItem,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Tooltip as MuiTooltip,
    Typography,
    useTheme,
} from '@mui/material';
import type { OptionSchema } from 'c-components';
import { AutoGrid, Button, Filterdropdown } from 'c-components';
import PannableSlider from 'c-components/PannableSlider';
import { useUrlState, useUrlStateValue } from 'c-hooks';
import ChartWrapperControls from 'c-reports/Components/ReportControlsWrapper/ChartWrapperControls';
import type { ChartReportPart } from 'c-reports/Types';
import { ReportType } from 'c-reports/Types';
import { useCommonTranslation } from 'c-translation';
import type { HeatmapLayerProps } from '@react-google-maps/api';
import { GoogleMap, useJsApiLoader } from '@react-google-maps/api';
import { DeckHeatmapLayer } from 'c-components/DeckHeatmapLayer';
import {
    Brush,
    Bar,
    CartesianGrid,
    ComposedChart,
    LabelList,
    Legend,
    Line,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import equal from 'fast-deep-equal';
import { ReportingMapOptions } from 'src/Modules/Main/Lib/Campaign/reporting';
import reportPdfUrl from './report_uniled_fetch_1.pdf?url';

type Anchor = HTMLElement | null;
type FilterField =
    | 'Event Name'
    | 'State'
    | 'County'
    | 'Town'
    | 'Zip'
    | 'Media Owner'
    | 'Display Env'
    | 'Display Format'
    | 'Frame'
    | 'Screen Name'
    | 'Type';
type Row = Record<string, string>;
type DatasetTab = 'spend' | 'exposure' | 'conversions';

const FILTER_FIELDS: FilterField[] = [
    'Event Name',
    'State',
    'County',
    'Town',
    'Zip',
    'Media Owner',
    'Display Env',
    'Display Format',
    'Frame',
    'Screen Name',
    'Type',
];
const FILTER_FIELDS_BY_DATASET: Record<DatasetTab, FilterField[]> = {
    spend: FILTER_FIELDS.filter(fieldName => fieldName !== 'Event Name'),
    exposure: FILTER_FIELDS,
    conversions: FILTER_FIELDS,
};

const FILTER_FIELD_TRANSLATION_KEYS: Record<FilterField, string> = {
    'Event Name':
        'Modules.Main.Campaigns.Overview.Analytics.conversionMeasurement.fields.eventName',
    State: 'Modules.Main.Campaigns.Overview.Analytics.conversionMeasurement.fields.state',
    County: 'Modules.Main.Campaigns.Overview.Analytics.conversionMeasurement.fields.county',
    Town: 'Modules.Main.Campaigns.Overview.Analytics.conversionMeasurement.fields.town',
    Zip: 'Modules.Main.Campaigns.Overview.Analytics.conversionMeasurement.fields.zip',
    'Media Owner':
        'Modules.Main.Campaigns.Overview.Analytics.conversionMeasurement.fields.mediaOwner',
    'Display Env':
        'Modules.Main.Campaigns.Overview.Analytics.conversionMeasurement.fields.displayEnv',
    'Display Format':
        'Modules.Main.Campaigns.Overview.Analytics.conversionMeasurement.fields.displayFormat',
    Frame: 'Modules.Main.Campaigns.Overview.Analytics.conversionMeasurement.fields.frame',
    'Screen Name':
        'Modules.Main.Campaigns.Overview.Analytics.conversionMeasurement.fields.screenName',
    Type: 'Modules.Main.Campaigns.Overview.Analytics.conversionMeasurement.fields.type',
};

const VIEW_BY_OPTIONS = ['Graph', 'Table', 'Map'];

const DATASET_FILTER_KEY_MAP: Record<DatasetTab, Partial<Record<FilterField, string>>> = {
    spend: { Frame: 'Meta' },
    exposure: { Frame: 'Unit' },
    conversions: {},
};

const parseCsv = (raw: string): Row[] => {
    const lines = raw
        .split(/\r?\n/)
        .map(l => l.trim())
        .filter(Boolean);

    if (lines.length < 2) return [];

    const headers = lines[0]
        .replace(/^\uFEFF/, '')
        .split(',')
        .map(h => h.trim());

    return lines.slice(1).map(line => {
        const values = line.split(',').map(v => v.trim());
        return headers.reduce((acc, h, i) => {
            acc[h] = values[i] ?? '';
            return acc;
        }, {} as Row);
    });
};

const uniqueSorted = (vals: string[]) =>
    Array.from(new Set(vals.filter(v => v != null && v !== ''))).sort((a, b) =>
        a.localeCompare(b, undefined, { numeric: true }),
    );

const createEmptyFilters = (): Record<FilterField, string[]> =>
    FILTER_FIELDS.reduce((acc, curr) => {
        acc[curr] = [];
        return acc;
    }, {} as Record<FilterField, string[]>);

const cloneFilters = (filters: Record<FilterField, string[]>): Record<FilterField, string[]> =>
    FILTER_FIELDS.reduce((acc, fieldName) => {
        acc[fieldName] = [...(filters[fieldName] ?? [])];
        return acc;
    }, {} as Record<FilterField, string[]>);

const sanitizePathSegment = (val: string) =>
    val
        .toLowerCase()
        .replaceAll(/[^a-z0-9]+/g, '_')
        .replaceAll(/^_+|_+$/g, '');

const normalizeFilters = (value: unknown): Record<FilterField, string[]> => {
    const empty = createEmptyFilters();
    if (value == null || typeof value !== 'object') return empty;

    return FILTER_FIELDS.reduce((acc, fieldName) => {
        const nextValue = (value as Record<string, unknown>)[fieldName];
        acc[fieldName] = Array.isArray(nextValue)
            ? nextValue.filter((entry): entry is string => typeof entry === 'string')
            : [];
        return acc;
    }, empty);
};

const GROUP_BY_FIELDS: FilterField[] = [
    'Frame',
    'Screen Name',
    'State',
    'County',
    'Town',
    'Zip',
    'Media Owner',
    'Display Env',
    'Display Format',
];
const EXCLUDED_TABLE_HEADERS = [
    'Display Lat',
    'Display Long',
    'Display Surface Area',
    'Event Name',
    'Type',
];
const EXPECTED_HEADERS = ['Expected Impressions', 'Expected Impressons'];
const DELIVERED_HEADERS = ['Delivered Impressions'];
const DELIVERY_PERCENTAGE_HEADER = 'Delivery %';
const CONVERSION_RATE_HEADER = 'Conversion Rate';
const DATASET_UNIQUE_HEADERS: Record<DatasetTab, string[]> = {
    spend: ['Average Spend', 'Total Spend', 'Volume'],
    exposure: ['Exposure Count'],
    conversions: [
        'Unique Devices Exposed',
        'Conversions',
        CONVERSION_RATE_HEADER,
        'Conversion Index',
    ],
};
const TABLE_NUMERIC_HEADERS = new Set([
    ...EXPECTED_HEADERS,
    ...DELIVERED_HEADERS,
    ...Object.values(DATASET_UNIQUE_HEADERS).flat(),
]);
const DEFAULT_MAP_METRIC: Record<DatasetTab, string> = {
    spend: 'Average Spend',
    exposure: 'Exposure Count',
    conversions: 'Conversions',
};
const MAP_METRIC_OPTIONS: Record<DatasetTab, string[]> = {
    spend: ['Average Spend', 'Total Spend', 'Volume', ...EXPECTED_HEADERS, ...DELIVERED_HEADERS],
    exposure: ['Exposure Count', ...EXPECTED_HEADERS, ...DELIVERED_HEADERS],
    conversions: [
        'Unique Devices Exposed',
        'Conversions',
        CONVERSION_RATE_HEADER,
        'Conversion Index',
        ...EXPECTED_HEADERS,
        ...DELIVERED_HEADERS,
    ],
};

const parseNumeric = (value: string): { number: number; isPercent: boolean } | null => {
    if (value == null) return null;
    const raw = value.trim();
    if (raw === '') return null;
    const isPercent = raw.endsWith('%');
    const normalized = raw.replaceAll(',', '').replace('%', '');
    const parsed = Number(normalized);
    if (Number.isNaN(parsed)) return null;
    return { number: parsed, isPercent };
};
const formatTwoDpMaxNumber = (value: number): string =>
    value.toLocaleString(undefined, { maximumFractionDigits: 2 });
const formatWholeNumber = (value: number | string): string =>
    Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
const formatPreciseNumber = (value: number): string =>
    value.toLocaleString(undefined, { maximumFractionDigits: 20 });

const wrapAxisLabel = (raw: string, maxLen = 16, maxLines = 3): string[] => {
    const words = String(raw ?? '')
        .replaceAll('|', '| ')
        .split(/\s+/)
        .filter(Boolean);
    const lines: string[] = [];
    let current = '';
    words.forEach(word => {
        let rest = word;
        while (rest.length > maxLen) {
            if (current) {
                lines.push(current);
                current = '';
            }
            lines.push(rest.slice(0, maxLen));
            rest = rest.slice(maxLen);
        }
        if (!current) current = rest;
        else if (`${current} ${rest}`.length <= maxLen) current = `${current} ${rest}`;
        else {
            lines.push(current);
            current = rest;
        }
    });
    if (current) lines.push(current);
    if (lines.length <= maxLines) return lines;
    const trimmed = lines.slice(0, maxLines);
    trimmed[maxLines - 1] = `${trimmed[maxLines - 1].slice(0, maxLen - 1)}…`;
    return trimmed;
};

const AngledWrappedTick = ({ x = 0, y = 0, payload }: any) => {
    const lines = wrapAxisLabel(String(payload?.value ?? ''));
    return (
        <g transform={`translate(${x},${y})`}>
            <text transform="rotate(-35)" textAnchor="end" fill="#666" fontSize={11}>
                {lines.map((line, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <tspan key={`${line}-${index}`} x={0} dy={index === 0 ? 8 : 12}>
                        {line}
                    </tspan>
                ))}
            </text>
        </g>
    );
};
const getDerivedConversionRate = (row: Row): number | null => {
    const uniqueDevicesExposed = toNumber(row['Unique Devices Exposed'] ?? '');
    const conversions = toNumber(row.Conversions ?? '');

    if (uniqueDevicesExposed <= 0) return null;

    return (conversions / uniqueDevicesExposed) * 100;
};
const getMetricValue = (row: Row, header: string, dataset: DatasetTab): number => {
    if (header === CONVERSION_RATE_HEADER && dataset === 'conversions') {
        return getDerivedConversionRate(row) ?? 0;
    }

    return toNumber(row[header] ?? '');
};

const aggregateValues = (values: string[]): string => {
    const nonEmpty = values.filter(v => v != null && v !== '');
    if (nonEmpty.length === 0) return '';
    if (nonEmpty.every(v => v === nonEmpty[0])) return nonEmpty[0];

    const numericValues = nonEmpty
        .map(parseNumeric)
        .filter((v): v is { number: number; isPercent: boolean } => v != null);
    if (numericValues.length === nonEmpty.length) {
        const isPercentSeries = numericValues.every(v => v.isPercent);
        if (isPercentSeries) {
            const avg = numericValues.reduce((sum, v) => sum + v.number, 0) / numericValues.length;
            return `${formatPreciseNumber(avg)}%`;
        }
        const sum = numericValues.reduce((acc, v) => acc + v.number, 0);
        return Number.isInteger(sum) ? String(sum) : sum.toFixed(2);
    }

    return 'Multiple';
};

const toNumber = (value: string): number => parseNumeric(value)?.number ?? 0;
const average = (values: number[]): number =>
    values.length === 0 ? 0 : values.reduce((sum, value) => sum + value, 0) / values.length;
const formatNumber = (value: number | string): string => {
    const numericValue = Number(value || 0);
    const absValue = Math.abs(numericValue);
    if (absValue >= 1_000_000_000) {
        const inBillions = numericValue / 1_000_000_000;
        const rounded = Number.isInteger(inBillions) ? `${inBillions}` : inBillions.toFixed(1);
        return `${rounded}B`;
    }
    if (absValue >= 1_000_000) {
        const inMillions = numericValue / 1_000_000;
        const rounded = Number.isInteger(inMillions) ? `${inMillions}` : inMillions.toFixed(1);
        return `${rounded}M`;
    }
    if (absValue >= 1_000) {
        const inThousands = numericValue / 1_000;
        const rounded = Number.isInteger(inThousands) ? `${inThousands}` : inThousands.toFixed(1);
        return `${rounded}K`;
    }
    return numericValue.toLocaleString();
};
const formatPercent = (value: number | string): string => `${Number(value || 0).toFixed(2)}%`;
const formatTwoDpMaxPercent = (value: number | string): string =>
    `${formatTwoDpMaxNumber(Number(value || 0))}%`;
const formatTableValue = (header: string, value: string): string => {
    if (!TABLE_NUMERIC_HEADERS.has(header)) return value;

    const parsedValue = parseNumeric(value);
    if (parsedValue == null) return value;

    const numericValue =
        header === CONVERSION_RATE_HEADER ? parsedValue.number * 100 : parsedValue.number;

    const normalizedValue = value.trim().replaceAll(',', '').replace('%', '');
    const decimalPlaces = normalizedValue.includes('.')
        ? normalizedValue.split('.')[1]?.length ?? 0
        : 0;
    const formattedValue =
        header === CONVERSION_RATE_HEADER
            ? formatTwoDpMaxNumber(numericValue)
            : EXPECTED_HEADERS.includes(header) || DELIVERED_HEADERS.includes(header)
            ? formatWholeNumber(numericValue)
            : numericValue.toLocaleString(undefined, {
                  minimumFractionDigits: decimalPlaces,
                  maximumFractionDigits: decimalPlaces,
              });

    return parsedValue.isPercent ? `${formattedValue}%` : formattedValue;
};

type Props = {
    rawCsv: string;
    dataset: DatasetTab;
    lockedEventName?: string;
    rateInTooltipOnly?: boolean;
    footfall?: boolean;
};

const ConversionMeasurementDatasetPanel: React.FC<Props> = ({
    rawCsv,
    dataset,
    lockedEventName,
    rateInTooltipOnly = false,
    footfall = false,
}) => {
    const theme = useTheme();
    const t = useCommonTranslation();
    const googleMapsApiKey = import.meta.env.VITE_GOOGLE_MAPS_KEY ?? '';
    const { isLoaded: isMapLoaded, loadError: mapLoadError } = useJsApiLoader({
        id: 'conversion-measurement-map',
        googleMapsApiKey,
        libraries: ['visualization'],
    });
    const mapRef = useRef<google.maps.Map | null>(null);
    const [groupAnchor, setGroupAnchor] = useState<Anchor>(null);
    const [viewAnchor, setViewAnchor] = useState<Anchor>(null);
    const { push, replaceMany } = useUrlState();
    const urlStatePath = useMemo(
        () =>
            lockedEventName == null
                ? `cm.${dataset}`
                : `cm.${dataset}.${sanitizePathSegment(lockedEventName)}`,
        [dataset, lockedEventName],
    );
    const viewByStatePath = 'cm.viewBy';
    const emptyFilters = useMemo(() => createEmptyFilters(), []);
    const urlViewBy = useUrlStateValue<string>(viewByStatePath, VIEW_BY_OPTIONS[0]);
    const defaultGroupBy = footfall ? 'Screen Name' : 'Frame';
    const urlGroupBy = useUrlStateValue<string>(`${urlStatePath}.groupBy`, defaultGroupBy);
    const mapMetricStatePath = `${urlStatePath}.mapMetric`;
    const urlMapMetric = useUrlStateValue<string>(mapMetricStatePath, DEFAULT_MAP_METRIC[dataset]);
    const urlAppliedFilters = useUrlStateValue<Record<string, unknown>>(
        `${urlStatePath}.filters`,
        emptyFilters,
    );
    const [groupBy, setGroupBy] = useState<string>(urlGroupBy ?? defaultGroupBy);
    const viewBy = VIEW_BY_OPTIONS.includes(urlViewBy ?? '')
        ? urlViewBy ?? VIEW_BY_OPTIONS[0]
        : 'Graph';
    const [sortMode, setSortMode] = useState<'asc' | 'desc' | 'alpha' | null>('desc');
    const [sortByKey, setSortByKey] = useState<string>('');
    const [tableSort, setTableSort] = useState<{ key: string; dir: 'asc' | 'desc' } | null>(null);
    const [seriesFilter, setSeriesFilter] = useState<'Expected' | 'Delivered' | 'Both'>('Both');
    const [showNumberLabels, setShowNumberLabels] = useState(false);
    const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
    const [selectedFilters, setSelectedFilters] = useState<Record<FilterField, string[]>>(
        normalizeFilters(urlAppliedFilters),
    );
    const [appliedFilters, setAppliedFilters] = useState<Record<FilterField, string[]>>(
        normalizeFilters(urlAppliedFilters),
    );

    useEffect(() => {
        setGroupBy(currentValue =>
            currentValue === urlGroupBy ? currentValue : urlGroupBy ?? defaultGroupBy,
        );
    }, [urlGroupBy, defaultGroupBy]);

    useEffect(() => {
        const normalized = normalizeFilters(urlAppliedFilters);
        setSelectedFilters(currentValue =>
            equal(currentValue, normalized) ? currentValue : cloneFilters(normalized),
        );
        setAppliedFilters(currentValue =>
            equal(currentValue, normalized) ? currentValue : cloneFilters(normalized),
        );
    }, [urlAppliedFilters]);

    const parsedRows = useMemo(() => parseCsv(rawCsv), [rawCsv]);
    const rows = useMemo(
        () =>
            lockedEventName == null
                ? parsedRows
                : parsedRows.filter(row => (row['Event Name'] ?? '') === lockedEventName),
        [parsedRows, lockedEventName],
    );

    const getFilterValue = useCallback(
        (row: Row, fieldName: FilterField) => {
            const mappedField = DATASET_FILTER_KEY_MAP[dataset][fieldName] ?? fieldName;
            return row[mappedField] ?? '';
        },
        [dataset],
    );
    const getDisplayHeaderLabel = useCallback(
        (header: string) => {
            if (dataset === 'spend' && header === 'Meta') {
                return t('CommonWords.Frame');
            }
            if (header === 'Frame' || header === 'Unit') {
                return t('CommonWords.Frame');
            }
            if (header === 'State') {
                return t('CommonWords.TVRegion');
            }
            if (header === 'Media Owner') {
                return t('CommonWords.MediaOwner');
            }
            if (EXPECTED_HEADERS.includes(header)) {
                return `Expected ${t('CommonWords.Impacts')}`;
            }
            if (DELIVERED_HEADERS.includes(header)) {
                return `Delivered ${t('CommonWords.Impacts')}`;
            }
            return header;
        },
        [dataset, t],
    );
    const getFilterFieldLabel = useCallback(
        (fieldName: FilterField) => t(FILTER_FIELD_TRANSLATION_KEYS[fieldName]),
        [t],
    );
    const datasetFilterFields = useMemo(() => FILTER_FIELDS_BY_DATASET[dataset], [dataset]);

    const availableFilterFields = useMemo(
        () =>
            datasetFilterFields.filter(fieldName => {
                if (lockedEventName != null && fieldName === 'Event Name') return false;
                const mappedField = DATASET_FILTER_KEY_MAP[dataset][fieldName] ?? fieldName;
                return rows.some(row => (row[mappedField] ?? '') !== '');
            }),
        [dataset, datasetFilterFields, rows, lockedEventName],
    );

    const optionsByField = useMemo<Record<FilterField, string[]>>(
        () =>
            datasetFilterFields.reduce((acc, fieldName) => {
                acc[fieldName] = uniqueSorted(rows.map(r => getFilterValue(r, fieldName)));
                return acc;
            }, {} as Record<FilterField, string[]>),
        [datasetFilterFields, rows, getFilterValue],
    );
    const dropdownOptionsByField = useMemo<Record<FilterField, OptionSchema[]>>(
        () =>
            datasetFilterFields.reduce((acc, fieldName) => {
                acc[fieldName] = optionsByField[fieldName].map(option => ({
                    label: option,
                    value: option,
                }));
                return acc;
            }, {} as Record<FilterField, OptionSchema[]>),
        [datasetFilterFields, optionsByField],
    );

    const groupByOptions = useMemo(
        () =>
            availableFilterFields.filter(
                fieldName =>
                    fieldName !== 'Event Name' &&
                    fieldName !== 'Type' &&
                    !(footfall && fieldName === 'Display Env') &&
                    optionsByField[fieldName].length > 0,
            ),
        [availableFilterFields, optionsByField, footfall],
    );

    const activeGroupBy = useMemo(
        () => (groupByOptions.includes(groupBy as FilterField) ? groupBy : groupByOptions[0] ?? ''),
        [groupBy, groupByOptions],
    );
    const activeGroupByHeader = useMemo(
        () =>
            activeGroupBy === ''
                ? ''
                : DATASET_FILTER_KEY_MAP[dataset][activeGroupBy as FilterField] ?? activeGroupBy,
        [dataset, activeGroupBy],
    );

    const activeFiltersCount = useMemo(
        () => Object.values(appliedFilters).filter(values => values.length > 0).length,
        [appliedFilters],
    );

    const activeFilterChips = useMemo(
        () =>
            availableFilterFields.flatMap(fieldName =>
                appliedFilters[fieldName].map(value => ({
                    key: `${fieldName}-${value}`,
                    field: fieldName,
                    value,
                })),
            ),
        [availableFilterFields, appliedFilters],
    );

    const tableHeaders = useMemo<string[]>(() => {
        if (rows.length === 0) return [];
        const preferredOrder = [
            'Event Name',
            'Frame',
            'State',
            'County',
            'Town',
            'Zip',
            'Media Owner',
            'Display Env',
            'Display Format',
        ];
        const allHeaders = Array.from(
            rows.reduce((acc, row) => {
                Object.keys(row).forEach(key => acc.add(key));
                return acc;
            }, new Set<string>()),
        );
        const preferredHeaders = preferredOrder.filter(h => allHeaders.includes(h));
        const otherHeaders = allHeaders
            .filter(h => !preferredHeaders.includes(h))
            .sort((a, b) => a.localeCompare(b));
        return [...preferredHeaders, ...otherHeaders];
    }, [rows]);
    const expectedColumnHeader = useMemo(
        () => EXPECTED_HEADERS.find(header => tableHeaders.includes(header)) ?? '',
        [tableHeaders],
    );
    const deliveredColumnHeader = useMemo(
        () => DELIVERED_HEADERS.find(header => tableHeaders.includes(header)) ?? '',
        [tableHeaders],
    );
    const emptyDataHeaders = useMemo(() => {
        const empties = new Set<string>();
        if (rows.length === 0) return empties;
        tableHeaders.forEach(header => {
            const hasData = rows.some(row => {
                const value = String(row[header] ?? '').trim();
                return value !== '' && value !== '-';
            });
            if (!hasData) empties.add(header);
        });
        return empties;
    }, [rows, tableHeaders]);
    const visibleHeaders = useMemo(() => {
        const groupedHeaderList = GROUP_BY_FIELDS.map(
            fieldName => DATASET_FILTER_KEY_MAP[dataset][fieldName] ?? fieldName,
        );
        const groupedHeaderKeys = new Set(groupedHeaderList);
        const baseHeaders = tableHeaders.filter(
            header =>
                !EXCLUDED_TABLE_HEADERS.includes(header) &&
                !(footfall && header === 'Display Env') &&
                (header === activeGroupByHeader || !emptyDataHeaders.has(header)),
        );
        const datasetUniqueHeaders = DATASET_UNIQUE_HEADERS[dataset].filter(header =>
            baseHeaders.includes(header),
        );
        const prioritizedHeaders = [
            expectedColumnHeader,
            deliveredColumnHeader,
            ...datasetUniqueHeaders,
        ].filter(header => header !== '' && !emptyDataHeaders.has(header));

        const remainingHeaders = baseHeaders.filter(
            header =>
                !prioritizedHeaders.includes(header) &&
                header !== activeGroupByHeader &&
                !groupedHeaderKeys.has(header),
        );
        const trailingGroupHeaders =
            activeGroupBy === 'Frame'
                ? groupedHeaderList.filter(
                      header => header !== activeGroupByHeader && baseHeaders.includes(header),
                  )
                : [];
        const deliveryHeader =
            expectedColumnHeader !== '' && deliveredColumnHeader !== ''
                ? [DELIVERY_PERCENTAGE_HEADER]
                : [];

        return [
            ...(activeGroupByHeader === '' ? [] : [activeGroupByHeader]),
            ...prioritizedHeaders.slice(0, 2),
            ...deliveryHeader,
            ...prioritizedHeaders.slice(2),
            ...remainingHeaders,
            ...trailingGroupHeaders,
        ];
    }, [
        tableHeaders,
        dataset,
        activeGroupBy,
        activeGroupByHeader,
        expectedColumnHeader,
        deliveredColumnHeader,
        emptyDataHeaders,
        footfall,
    ]);
    const mapMetricOptions = useMemo(
        () =>
            MAP_METRIC_OPTIONS[dataset].filter(
                header =>
                    rows.some(row => getMetricValue(row, header, dataset) > 0) ||
                    header === DEFAULT_MAP_METRIC[dataset],
            ),
        [dataset, rows],
    );
    const selectedMapMetric = useMemo(
        () =>
            mapMetricOptions.includes(urlMapMetric)
                ? urlMapMetric
                : mapMetricOptions[0] ?? DEFAULT_MAP_METRIC[dataset],
        [dataset, mapMetricOptions, urlMapMetric],
    );

    const filteredRows = useMemo(
        () =>
            rows.filter(row =>
                availableFilterFields.every(fieldName => {
                    const selectedValues = appliedFilters[fieldName];
                    if (selectedValues.length === 0) return true;
                    return selectedValues.includes(getFilterValue(row, fieldName));
                }),
            ),
        [rows, appliedFilters, availableFilterFields, getFilterValue],
    );
    const groupedRows = useMemo(() => {
        if (activeGroupByHeader === '') return filteredRows;

        const groupedMap = filteredRows.reduce((acc, row) => {
            const groupVal = row[activeGroupByHeader] ?? '';
            if (!acc.has(groupVal)) acc.set(groupVal, []);
            acc.get(groupVal)?.push(row);
            return acc;
        }, new Map<string, Row[]>());

        return Array.from(groupedMap.entries()).map(([groupVal, groupRows]) =>
            visibleHeaders.reduce((acc, header) => {
                if (header === activeGroupByHeader) {
                    acc[header] = groupVal;
                } else if (header === CONVERSION_RATE_HEADER && dataset === 'conversions') {
                    const conversionRate = getDerivedConversionRate({
                        'Unique Devices Exposed': String(
                            groupRows.reduce(
                                (sum, row) => sum + toNumber(row['Unique Devices Exposed'] ?? ''),
                                0,
                            ),
                        ),
                        Conversions: String(
                            groupRows.reduce(
                                (sum, row) => sum + toNumber(row.Conversions ?? ''),
                                0,
                            ),
                        ),
                    } as Row);
                    acc[header] =
                        conversionRate == null ? '' : `${formatTwoDpMaxNumber(conversionRate)}%`;
                } else {
                    acc[header] = aggregateValues(groupRows.map(r => r[header] ?? ''));
                }
                return acc;
            }, {} as Row),
        );
    }, [activeGroupByHeader, dataset, filteredRows, visibleHeaders]);
    const getDeliveryPercent = useCallback(
        (row: Row) => {
            if (expectedColumnHeader === '' || deliveredColumnHeader === '') {
                return { label: '-', color: undefined as string | undefined };
            }
            const expected = toNumber(row[expectedColumnHeader] ?? '');
            const delivered = toNumber(row[deliveredColumnHeader] ?? '');
            if (expected <= 0) return { label: '-', color: undefined as string | undefined };
            const percent = (delivered / expected) * 100;
            const label = `${percent.toFixed(2)}%`;
            if (percent > 100) return { label, color: theme.palette.success.main };
            if (percent < 100) return { label, color: theme.palette.error.main };
            return { label, color: undefined as string | undefined };
        },
        [
            deliveredColumnHeader,
            expectedColumnHeader,
            theme.palette.error.main,
            theme.palette.success.main,
        ],
    );

    const exposureGraphData = useMemo(
        () =>
            groupedRows
                .map(row => ({
                    name: row[activeGroupByHeader] ?? '',
                    expectedImpressions: toNumber(row['Expected Impressions'] ?? ''),
                    deliveredImpressions: toNumber(row['Delivered Impressions'] ?? ''),
                    exposureCount: toNumber(row['Exposure Count'] ?? ''),
                }))
                .filter(
                    point =>
                        point.name !== '' &&
                        (point.expectedImpressions !== 0 ||
                            point.deliveredImpressions !== 0 ||
                            point.exposureCount !== 0),
                )
                .sort((a, b) => b.exposureCount - a.exposureCount),
        [groupedRows, activeGroupByHeader],
    );
    const spendGraphData = useMemo(() => {
        if (activeGroupByHeader === '') return [];

        const groupedMap = filteredRows.reduce((acc, row) => {
            const groupVal = row[activeGroupByHeader] ?? '';
            if (!acc.has(groupVal)) acc.set(groupVal, []);
            acc.get(groupVal)?.push(row);
            return acc;
        }, new Map<string, Row[]>());

        return Array.from(groupedMap.entries())
            .map(([groupVal, groupRows]) => {
                const averageSpendValues = groupRows
                    .map(row => toNumber(row['Average Spend'] ?? ''))
                    .filter(value => value !== 0);
                return {
                    name: groupVal,
                    expectedImpressions: groupRows.reduce(
                        (sum, row) => sum + toNumber(row['Expected Impressions'] ?? ''),
                        0,
                    ),
                    deliveredImpressions: groupRows.reduce(
                        (sum, row) => sum + toNumber(row['Delivered Impressions'] ?? ''),
                        0,
                    ),
                    averageSpend: average(averageSpendValues),
                };
            })
            .filter(
                point =>
                    point.name !== '' &&
                    (point.expectedImpressions !== 0 ||
                        point.deliveredImpressions !== 0 ||
                        point.averageSpend !== 0),
            )
            .sort((a, b) => b.averageSpend - a.averageSpend);
    }, [filteredRows, activeGroupByHeader]);
    const conversionsGraphData = useMemo(() => {
        if (activeGroupByHeader === '') return [];

        const groupedMap = filteredRows.reduce((acc, row) => {
            const groupVal = row[activeGroupByHeader] ?? '';
            if (!acc.has(groupVal)) acc.set(groupVal, []);
            acc.get(groupVal)?.push(row);
            return acc;
        }, new Map<string, Row[]>());

        return Array.from(groupedMap.entries())
            .map(([groupVal, groupRows]) => {
                const uniqueDevicesExposed = groupRows.reduce(
                    (sum, row) => sum + toNumber(row['Unique Devices Exposed'] ?? ''),
                    0,
                );
                const conversions = groupRows.reduce(
                    (sum, row) => sum + toNumber(row.Conversions ?? ''),
                    0,
                );
                return {
                    name: groupVal,
                    expectedImpressions: groupRows.reduce(
                        (sum, row) => sum + toNumber(row['Expected Impressons'] ?? ''),
                        0,
                    ),
                    deliveredImpressions: groupRows.reduce(
                        (sum, row) => sum + toNumber(row['Delivered Impressions'] ?? ''),
                        0,
                    ),
                    conversions,
                    conversionRate:
                        uniqueDevicesExposed > 0 ? (conversions / uniqueDevicesExposed) * 100 : 0,
                };
            })
            .filter(
                point =>
                    point.name !== '' &&
                    (point.expectedImpressions !== 0 ||
                        point.deliveredImpressions !== 0 ||
                        point.conversions !== 0 ||
                        point.conversionRate !== 0),
            )
            .sort((a, b) => b.conversions - a.conversions);
    }, [filteredRows, activeGroupByHeader]);
    const showExposureGraph = dataset === 'exposure' && viewBy === 'Graph';
    const showSpendGraph = dataset === 'spend' && viewBy === 'Graph';
    const showConversionsGraph = dataset === 'conversions' && viewBy === 'Graph';
    const showGraph = showExposureGraph || showSpendGraph || showConversionsGraph;
    const showMap = viewBy === 'Map';
    const secondaryLineKey = useMemo(
        () =>
            showConversionsGraph
                ? 'conversionRate'
                : showSpendGraph
                ? 'averageSpend'
                : 'exposureCount',
        [showConversionsGraph, showSpendGraph],
    );
    const graphDataBase = useMemo(
        () =>
            showExposureGraph
                ? exposureGraphData
                : showSpendGraph
                ? spendGraphData
                : conversionsGraphData,
        [
            showExposureGraph,
            showSpendGraph,
            exposureGraphData,
            spendGraphData,
            conversionsGraphData,
        ],
    );
    const sortByOptions = useMemo<{ value: string; label: string }[]>(() => {
        if (showConversionsGraph) {
            return [
                { value: 'conversions', label: 'Conversions' },
                ...(rateInTooltipOnly
                    ? []
                    : [{ value: 'conversionRate', label: 'Conversion Rate' }]),
                { value: 'expectedImpressions', label: 'Expected' },
                { value: 'deliveredImpressions', label: 'Delivered' },
            ];
        }
        if (showSpendGraph) {
            return [
                { value: 'averageSpend', label: 'Average Spend' },
                { value: 'expectedImpressions', label: 'Expected' },
                { value: 'deliveredImpressions', label: 'Delivered' },
            ];
        }
        return [
            { value: 'exposureCount', label: 'Exposure Count' },
            { value: 'expectedImpressions', label: 'Expected' },
            { value: 'deliveredImpressions', label: 'Delivered' },
        ];
    }, [showConversionsGraph, showSpendGraph, rateInTooltipOnly]);
    const graphSortKey = useMemo(
        () => sortByKey || sortByOptions[0]?.value || 'name',
        [sortByKey, sortByOptions],
    );
    const graphData = useMemo(() => {
        const next = [...graphDataBase];
        if (sortMode === 'alpha') {
            next.sort((a, b) =>
                String(a.name ?? '').localeCompare(String(b.name ?? ''), undefined, {
                    numeric: true,
                }),
            );
            return next;
        }
        if (sortMode === 'asc') {
            next.sort((a, b) => Number(a[graphSortKey] ?? 0) - Number(b[graphSortKey] ?? 0));
            return next;
        }
        if (sortMode === 'desc') {
            next.sort((a, b) => Number(b[graphSortKey] ?? 0) - Number(a[graphSortKey] ?? 0));
            return next;
        }
        return next;
    }, [graphDataBase, graphSortKey, sortMode]);
    useEffect(() => {
        const availableKeys = sortByOptions.map(option => option.value);
        if (!availableKeys.includes(sortByKey)) {
            setSortByKey(availableKeys[0] ?? '');
            setSortMode('desc');
        }
    }, [sortByOptions, sortByKey]);
    const showExpectedSeries = seriesFilter !== 'Delivered';
    const showDeliveredSeries = seriesFilter !== 'Expected';
    const chartParts = useMemo<ChartReportPart[]>(() => {
        const base: ChartReportPart[] = [];
        if (showExpectedSeries) {
            base.push({
                dataKey: 'expectedImpressions',
                labelOverride: 'Expected',
                barProps: { fill: '#9e9e9e' },
            });
        }
        if (showDeliveredSeries) {
            base.push({
                dataKey: 'deliveredImpressions',
                labelOverride: 'Delivered',
                barProps: { fill: theme.palette.primary.main },
            });
        }
        if (showConversionsGraph) {
            base.push({
                dataKey: 'conversions',
                labelOverride: 'Conversions',
                lineProps: { stroke: theme.palette.secondary.main },
            });
            if (!rateInTooltipOnly) {
                base.push({
                    dataKey: 'conversionRate',
                    labelOverride: 'Conversion Rate',
                    lineProps: {
                        stroke: theme.palette.tertiary?.main ?? theme.palette.secondary.main,
                    },
                });
            }
        } else {
            base.push({
                dataKey: showSpendGraph ? 'averageSpend' : 'exposureCount',
                labelOverride: showSpendGraph ? 'Average Spend' : 'Exposure Count',
                lineProps: { stroke: theme.palette.secondary.main },
            });
        }
        return base;
    }, [
        showExpectedSeries,
        showDeliveredSeries,
        showConversionsGraph,
        showSpendGraph,
        rateInTooltipOnly,
        theme.palette.primary.main,
        theme.palette.secondary.main,
        theme.palette.tertiary?.main,
    ]);
    const adjustedEndIndex = useMemo(() => {
        const maxVisibleLabels = 15;
        const maxCharsPerLabel = 10;
        const avgLabelLength =
            graphData.reduce((sum, item) => sum + String(item.name ?? '').length, 0) /
            Math.max(1, graphData.length);

        if (avgLabelLength <= maxCharsPerLabel && graphData.length <= 24) {
            return Math.max(0, graphData.length - 1);
        }
        return Math.min(Math.max(0, graphData.length - 1), maxVisibleLabels);
    }, [graphData]);
    const [brushRange, setBrushRange] = useState<[number, number]>([0, adjustedEndIndex]);

    useEffect(() => {
        setBrushRange([0, adjustedEndIndex]);
    }, [adjustedEndIndex, graphData.length]);
    const formatGraphLabel = useCallback(
        (value: number | string, dataKey: string, row: Record<string, any>) => {
            if (showConversionsGraph && dataKey === 'conversionRate') {
                return formatTwoDpMaxPercent(value);
            }
            if (dataKey === 'expectedImpressions' || dataKey === 'deliveredImpressions') {
                return formatWholeNumber(value);
            }
            return formatNumber(value);
        },
        [showConversionsGraph],
    );
    const getTableCellValue = useCallback(
        (row: Row, header: string) => {
            if (header === CONVERSION_RATE_HEADER && dataset === 'conversions') {
                const conversionRate = getDerivedConversionRate(row);
                return conversionRate == null ? '-' : `${formatTwoDpMaxNumber(conversionRate)}%`;
            }

            return row[header] ?? '-';
        },
        [dataset],
    );
    const getSortValue = useCallback(
        (row: Row, header: string): number | string => {
            if (header === DELIVERY_PERCENTAGE_HEADER) {
                const parsed = parseFloat(getDeliveryPercent(row).label);
                return Number.isNaN(parsed) ? Number.NEGATIVE_INFINITY : parsed;
            }
            if (header === CONVERSION_RATE_HEADER && dataset === 'conversions') {
                const parsed = parseFloat(String(getTableCellValue(row, header)).replace('%', ''));
                return Number.isNaN(parsed) ? Number.NEGATIVE_INFINITY : parsed;
            }
            if (TABLE_NUMERIC_HEADERS.has(header)) {
                return toNumber(row[header] ?? '');
            }
            return String(row[header] ?? '');
        },
        [dataset, getDeliveryPercent, getTableCellValue],
    );
    const sortedGroupedRows = useMemo(() => {
        if (!tableSort) return groupedRows;
        const { key, dir } = tableSort;
        const factor = dir === 'asc' ? 1 : -1;
        return [...groupedRows].sort((a, b) => {
            const av = getSortValue(a, key);
            const bv = getSortValue(b, key);
            if (typeof av === 'number' && typeof bv === 'number') {
                return (av - bv) * factor;
            }
            return String(av).localeCompare(String(bv), undefined, { numeric: true }) * factor;
        });
    }, [groupedRows, tableSort, getSortValue]);
    const handleTableSort = useCallback((header: string) => {
        setTableSort(prev => {
            if (prev?.key !== header) return { key: header, dir: 'asc' };
            if (prev.dir === 'asc') return { key: header, dir: 'desc' };
            return null; // third click clears sorting
        });
    }, []);
    const resolveLabelPayload = useCallback((...args: any[]): Record<string, any> => {
        const objectArgs = args.filter(arg => arg != null && typeof arg === 'object');
        const payloadOwner = objectArgs.find(arg => {
            const payload = (arg as { payload?: unknown }).payload;
            return payload != null && typeof payload === 'object';
        }) as { payload?: Record<string, any> } | undefined;
        if (payloadOwner?.payload != null) {
            return payloadOwner.payload;
        }

        const rowLikeArg = objectArgs.find(arg => {
            const candidate = arg as Record<string, any>;
            return (
                'expectedImpressions' in candidate ||
                'deliveredImpressions' in candidate ||
                'conversions' in candidate ||
                'conversionRate' in candidate ||
                'averageSpend' in candidate ||
                'exposureCount' in candidate
            );
        }) as Record<string, any> | undefined;

        return rowLikeArg ?? {};
    }, []);
    const mapPoints = useMemo(
        () =>
            filteredRows
                .map((row, index) => {
                    const lat = Number(row['Display Lat']);
                    const lng = Number(row['Display Long']);
                    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
                    const weight = getMetricValue(row, selectedMapMetric, dataset);
                    if (weight <= 0) return null;
                    return {
                        id: `${index}-${row[activeGroupByHeader] ?? row.Frame ?? row.Unit ?? ''}`,
                        name: row[activeGroupByHeader] ?? row.Frame ?? row.Unit ?? '',
                        position: { lat, lng },
                        weight,
                    };
                })
                .filter(
                    (
                        point,
                    ): point is {
                        id: string;
                        name: string;
                        position: { lat: number; lng: number };
                        weight: number;
                    } => point != null,
                ),
        [activeGroupByHeader, dataset, filteredRows, selectedMapMetric],
    );
    const heatMapData = useMemo<HeatmapLayerProps['data']>(() => {
        if (!isMapLoaded || typeof google === 'undefined') return [];

        return mapPoints.map(point => ({
            location: new google.maps.LatLng(point.position.lat, point.position.lng),
            weight: point.weight,
        }));
    }, [isMapLoaded, mapPoints]);
    const mapCenter = useMemo(
        () => mapPoints[0]?.position ?? { lat: 39.8283, lng: -98.5795 },
        [mapPoints],
    );
    const onMapLoad = useCallback((map: google.maps.Map) => {
        mapRef.current = map;
    }, []);
    const onMapUnmount = useCallback(() => {
        mapRef.current = null;
    }, []);
    useEffect(() => {
        if (selectedMapMetric !== urlMapMetric) {
            replaceMany({ [mapMetricStatePath]: selectedMapMetric });
        }
    }, [mapMetricStatePath, replaceMany, selectedMapMetric, urlMapMetric]);
    useEffect(() => {
        if (mapRef.current == null || mapPoints.length === 0) return;

        const bounds = new google.maps.LatLngBounds();
        mapPoints.forEach(point => {
            bounds.extend(point.position);
        });
        mapRef.current.fitBounds(bounds);
    }, [mapPoints]);
    const onExportData = useCallback(
        (format: 'json' | 'csv') => {
            const seriesKeys = chartParts.map(p => p.dataKey);
            const rows = graphData.map(row =>
                seriesKeys.reduce(
                    (acc, key) => {
                        acc[key] = row[key];
                        return acc;
                    },
                    { name: row.name } as Record<string, any>,
                ),
            );

            if (format === 'json') {
                const blob = new Blob([JSON.stringify(rows, null, 2)], {
                    type: 'application/json',
                });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `conversion_measurement_${dataset}.json`;
                a.click();
                URL.revokeObjectURL(url);
                return;
            }

            const headers = ['name', ...seriesKeys];
            const csvRows = [
                headers.join(','),
                ...rows.map(row =>
                    headers.map(header => JSON.stringify(row[header] ?? '')).join(','),
                ),
            ];
            const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `conversion_measurement_${dataset}.csv`;
            a.click();
            URL.revokeObjectURL(url);
        },
        [chartParts, dataset, graphData],
    );
    const renderSmartLineLabel = useCallback(
        (dataKey: string) =>
            function SmartLineLabel(props: any) {
                const payload = resolveLabelPayload(props);
                const label = formatGraphLabel(props?.value, dataKey, payload);
                if (label === '' || label === '-') return null;

                const x = Number(props?.x ?? 0);
                const y = Number(props?.y ?? 0);
                const showBelow = y < 24;

                return (
                    <text
                        x={x}
                        y={y}
                        dy={showBelow ? 14 : -8}
                        textAnchor="middle"
                        fontSize={11}
                        fill="#616161"
                    >
                        {label}
                    </text>
                );
            },
        [formatGraphLabel, resolveLabelPayload],
    );

    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    gap: 1.5,
                    mb: 2,
                }}
            >
                {footfall && (
                    <Box sx={{ mr: 'auto' }}>
                        <MuiTooltip title="View report (PDF)">
                            <IconButton
                                color="primary"
                                onClick={() =>
                                    window.open(reportPdfUrl, '_blank', 'noopener,noreferrer')
                                }
                            >
                                <PictureAsPdf />
                            </IconButton>
                        </MuiTooltip>
                    </Box>
                )}
                <Stack direction="row" spacing={1} sx={{ ml: 'auto' }} flexWrap="wrap" useFlexGap>
                    <Badge
                        color="primary"
                        badgeContent={activeFiltersCount}
                        invisible={activeFiltersCount === 0}
                        overlap="rectangular"
                    >
                        <Button
                            className="cm-filter-by"
                            onClick={() => setShowAdvancedFilters(prev => !prev)}
                            color="secondary"
                        >
                            <FilterList sx={{ mr: 0.5 }} />
                            {t('Modules.Main.Campaigns.Overview.Reports.filterBy')}
                            <KeyboardArrowDown
                                sx={{
                                    transform: showAdvancedFilters
                                        ? 'rotate(180deg)'
                                        : 'rotate(0deg)',
                                    transition: 'transform 0.2s',
                                }}
                            />
                        </Button>
                    </Badge>

                    {!showMap && (
                        <Button
                            className="cm-group-by"
                            onClick={e => setGroupAnchor(e.currentTarget)}
                        >
                            <ViewWeek sx={{ mr: 0.5 }} />
                            {t('Modules.Main.Campaigns.Overview.Reports.byFieldButton')}
                            <KeyboardArrowDown />
                        </Button>
                    )}

                    <Button className="cm-view-by" onClick={e => setViewAnchor(e.currentTarget)}>
                        <ViewModule sx={{ mr: 0.5 }} />
                        {t('Modules.Main.Campaigns.Overview.Reports.viewBy')}
                        <KeyboardArrowDown />
                    </Button>
                </Stack>
            </Box>

            <Collapse in={showAdvancedFilters}>
                <Box sx={{ mb: 2, p: 2 }}>
                    <AutoGrid spacing={1} xs={6} sm={6} md={4} lg={3} xl={2}>
                        {availableFilterFields.map(fieldName => {
                            const values = selectedFilters[fieldName];
                            return (
                                <Box key={fieldName}>
                                    <Filterdropdown
                                        selectAllOption
                                        multiple
                                        options={dropdownOptionsByField[fieldName]}
                                        value={values}
                                        onChange={e => {
                                            const value = e as string[];
                                            setSelectedFilters(prev => ({
                                                ...prev,
                                                [fieldName]: value,
                                            }));
                                        }}
                                        placeholder={getFilterFieldLabel(fieldName)}
                                    />
                                </Box>
                            );
                        })}
                    </AutoGrid>

                    {activeFilterChips.length > 0 && (
                        <Stack
                            direction="row"
                            spacing={1}
                            sx={{ mt: 2 }}
                            flexWrap="wrap"
                            useFlexGap
                        >
                            {activeFilterChips.map(chip => (
                                <Chip
                                    key={chip.key}
                                    size="small"
                                    label={`${getFilterFieldLabel(chip.field)}: ${chip.value}`}
                                    onDelete={() => {
                                        const nextSelected = {
                                            ...selectedFilters,
                                            [chip.field]: selectedFilters[chip.field].filter(
                                                v => v !== chip.value,
                                            ),
                                        };
                                        setSelectedFilters(prev => ({
                                            ...prev,
                                            [chip.field]: prev[chip.field].filter(
                                                v => v !== chip.value,
                                            ),
                                        }));
                                        const nextApplied = cloneFilters(nextSelected);
                                        setAppliedFilters(nextApplied);
                                        push(`${urlStatePath}.filters`, nextApplied);
                                    }}
                                />
                            ))}
                        </Stack>
                    )}

                    <Stack
                        direction="row"
                        spacing={1}
                        sx={{ mt: 2, py: 0.75, justifyContent: 'center' }}
                    >
                        <Button
                            className="cm-reset-filters"
                            onClick={() => {
                                const empty = createEmptyFilters();
                                setSelectedFilters(empty);
                                setAppliedFilters(empty);
                                push(`${urlStatePath}.filters`, empty);
                            }}
                        >
                            {t('Modules.Main.Campaigns.Overview.Reports.advancedFiltering.reset')}
                        </Button>
                        <Button
                            className="cm-search"
                            onClick={() => {
                                const nextApplied = cloneFilters(selectedFilters);
                                setAppliedFilters(nextApplied);
                                push(`${urlStatePath}.filters`, nextApplied);
                            }}
                        >
                            {t('Modules.Main.Campaigns.Overview.Reports.advancedFiltering.search')}
                        </Button>
                    </Stack>
                </Box>
            </Collapse>

            <Menu
                anchorEl={groupAnchor}
                open={Boolean(groupAnchor)}
                onClose={() => setGroupAnchor(null)}
            >
                {groupByOptions.map(option => (
                    <MenuItem
                        key={option}
                        selected={activeGroupBy === option}
                        onClick={() => {
                            setGroupBy(option);
                            push(`${urlStatePath}.groupBy`, option);
                            setGroupAnchor(null);
                        }}
                    >
                        {getFilterFieldLabel(option)}
                    </MenuItem>
                ))}
            </Menu>

            <Menu
                anchorEl={viewAnchor}
                open={Boolean(viewAnchor)}
                onClose={() => setViewAnchor(null)}
            >
                {VIEW_BY_OPTIONS.map(option => (
                    <MenuItem
                        key={option}
                        selected={viewBy === option}
                        onClick={() => {
                            push(viewByStatePath, option);
                            setViewAnchor(null);
                        }}
                    >
                        {option === 'Graph' && <BarChart sx={{ mr: 1 }} fontSize="small" />}
                        {option === 'Table' && <TableView sx={{ mr: 1 }} fontSize="small" />}
                        {option}
                    </MenuItem>
                ))}
            </Menu>

            <Card sx={{ height: 'calc(100vh - 195px)' }}>
                <CardContent sx={{ height: '100%', p: 0, '&:last-child': { pb: 0 } }}>
                    {showGraph ? (
                        <Box
                            sx={{
                                height: '100%',
                                p: 2,
                                display: 'flex',
                                flexDirection: 'column',
                                minHeight: 0,
                            }}
                        >
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
                                <FormControl size="small" sx={{ minWidth: 180, mr: 1 }}>
                                    <InputLabel>Sort By</InputLabel>
                                    <Select
                                        value={graphSortKey}
                                        label="Sort By"
                                        onChange={e => setSortByKey(e.target.value)}
                                    >
                                        {sortByOptions.map(option => (
                                            <MenuItem key={option.value} value={option.value}>
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <ChartWrapperControls
                                    type={ReportType.Mixed}
                                    chartProps={{
                                        data: graphData,
                                        parts: chartParts,
                                        xAxisDataKey: 'name',
                                    }}
                                    namePrefix={`conversion_measurement_${dataset}`}
                                    startDate={new Date()}
                                    endDate={new Date()}
                                    allowedActions={[
                                        'download',
                                        'sortAsc',
                                        'sortDesc',
                                        'sortAlpha',
                                        'toggleNumbers',
                                        'filter',
                                        'exportData',
                                    ]}
                                    onSortAsc={() => setSortMode('asc')}
                                    onSortDesc={() => setSortMode('desc')}
                                    onSortAlpha={() => setSortMode('alpha')}
                                    onToggleNumbers={() => setShowNumberLabels(prev => !prev)}
                                    onFilterChange={setSeriesFilter}
                                    onExportData={onExportData}
                                    activeSort={sortMode}
                                    numbersOn={showNumberLabels}
                                    activeFilter={seriesFilter}
                                />
                            </Box>
                            <Box sx={{ flex: 1, minHeight: 0 }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <ComposedChart
                                        data={graphData}
                                        margin={{
                                            top: 44,
                                            right:
                                                showConversionsGraph && !rateInTooltipOnly
                                                    ? 100
                                                    : 36,
                                            left: 36,
                                            bottom: 12,
                                        }}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis
                                            dataKey="name"
                                            height={96}
                                            tickMargin={8}
                                            interval={0}
                                            tick={<AngledWrappedTick />}
                                        />
                                        <YAxis
                                            yAxisId="left"
                                            width={90}
                                            tickMargin={10}
                                            tickFormatter={formatWholeNumber}
                                            label={{
                                                value: 'Impressions',
                                                angle: -90,
                                                position: 'left',
                                                offset: 18,
                                            }}
                                        />
                                        <YAxis
                                            yAxisId="right"
                                            orientation="right"
                                            width={90}
                                            tickMargin={10}
                                            tickFormatter={formatNumber}
                                            label={{
                                                value: showExposureGraph
                                                    ? 'Exposure Count'
                                                    : showSpendGraph
                                                    ? 'Average Spend'
                                                    : 'Conversions',
                                                angle: 90,
                                                position: 'right',
                                                offset: 0,
                                            }}
                                        />
                                        {showConversionsGraph && (
                                            <YAxis
                                                yAxisId="rightRate"
                                                orientation="right"
                                                hide={rateInTooltipOnly}
                                                width={rateInTooltipOnly ? 0 : 90}
                                                tickMargin={10}
                                                tick={{ dx: 10 }}
                                                tickFormatter={formatTwoDpMaxPercent}
                                                axisLine={false}
                                                tickLine={false}
                                                label={
                                                    rateInTooltipOnly
                                                        ? undefined
                                                        : {
                                                              value: 'Conversion Rate',
                                                              angle: 90,
                                                              position: 'right',
                                                              offset: 50,
                                                          }
                                                }
                                            />
                                        )}
                                        <Tooltip
                                            formatter={(value: number | string, name: string) => {
                                                if (name === 'Expected' || name === 'Delivered') {
                                                    return formatWholeNumber(value);
                                                }

                                                if (name === 'Conversion Rate') {
                                                    return formatTwoDpMaxPercent(value);
                                                }

                                                return formatNumber(value);
                                            }}
                                        />
                                        <Legend verticalAlign="top" align="right" height={40} />
                                        {showExpectedSeries && (
                                            <Bar
                                                yAxisId="left"
                                                dataKey="expectedImpressions"
                                                name="Expected"
                                                fill="#9e9e9e"
                                            >
                                                {showNumberLabels && (
                                                    <LabelList
                                                        dataKey="expectedImpressions"
                                                        position="top"
                                                        formatter={(value, ...rest) =>
                                                            formatGraphLabel(
                                                                value as number,
                                                                'expectedImpressions',
                                                                resolveLabelPayload(...rest),
                                                            )
                                                        }
                                                    />
                                                )}
                                            </Bar>
                                        )}
                                        {showDeliveredSeries && (
                                            <Bar
                                                yAxisId="left"
                                                dataKey="deliveredImpressions"
                                                name="Delivered"
                                                fill={theme.palette.primary.main}
                                            >
                                                {showNumberLabels && (
                                                    <LabelList
                                                        dataKey="deliveredImpressions"
                                                        position="top"
                                                        formatter={(value, ...rest) =>
                                                            formatGraphLabel(
                                                                value as number,
                                                                'deliveredImpressions',
                                                                resolveLabelPayload(...rest),
                                                            )
                                                        }
                                                    />
                                                )}
                                            </Bar>
                                        )}
                                        {showConversionsGraph && (
                                            <Line
                                                yAxisId="right"
                                                dataKey="conversions"
                                                name="Conversions"
                                                stroke={theme.palette.secondary.main}
                                                strokeWidth={2}
                                                dot={{ r: 2 }}
                                            >
                                                {showNumberLabels && (
                                                    <LabelList
                                                        dataKey="conversions"
                                                        content={renderSmartLineLabel(
                                                            'conversions',
                                                        )}
                                                    />
                                                )}
                                            </Line>
                                        )}
                                        <Line
                                            yAxisId={showConversionsGraph ? 'rightRate' : 'right'}
                                            dataKey={
                                                showExposureGraph
                                                    ? 'exposureCount'
                                                    : showSpendGraph
                                                    ? 'averageSpend'
                                                    : 'conversionRate'
                                            }
                                            name={
                                                showExposureGraph
                                                    ? 'Exposure Count'
                                                    : showSpendGraph
                                                    ? 'Average Spend'
                                                    : 'Conversion Rate'
                                            }
                                            stroke={
                                                showConversionsGraph
                                                    ? theme.palette.tertiary?.main ??
                                                      theme.palette.secondary.main
                                                    : theme.palette.secondary.main
                                            }
                                            strokeWidth={2}
                                            strokeOpacity={
                                                showConversionsGraph && rateInTooltipOnly ? 0 : 1
                                            }
                                            dot={
                                                showConversionsGraph && rateInTooltipOnly
                                                    ? false
                                                    : { r: 2 }
                                            }
                                            activeDot={
                                                showConversionsGraph && rateInTooltipOnly
                                                    ? false
                                                    : undefined
                                            }
                                            legendType={
                                                showConversionsGraph && rateInTooltipOnly
                                                    ? 'none'
                                                    : undefined
                                            }
                                        >
                                            {showNumberLabels &&
                                                !(showConversionsGraph && rateInTooltipOnly) && (
                                                    <LabelList
                                                        dataKey={secondaryLineKey}
                                                        content={renderSmartLineLabel(
                                                            secondaryLineKey,
                                                        )}
                                                    />
                                                )}
                                        </Line>
                                        <Brush
                                            dataKey="name"
                                            height={0}
                                            travellerWidth={0}
                                            startIndex={Math.floor(brushRange[0])}
                                            endIndex={Math.ceil(brushRange[1])}
                                            stroke="transparent"
                                            fill="transparent"
                                        />
                                    </ComposedChart>
                                </ResponsiveContainer>
                            </Box>
                            {graphData.length > 1 && (
                                <Box pl={9} width="95%" mt={0.5} mb={5}>
                                    <PannableSlider
                                        value={brushRange}
                                        onChange={setBrushRange}
                                        min={0}
                                        max={Math.max(0, graphData.length - 1)}
                                        getLabelForValue={i => (graphData[i]?.name ?? i) as string}
                                    />
                                </Box>
                            )}
                        </Box>
                    ) : showMap ? (
                        <Box
                            sx={{ height: '100%', p: 2, display: 'flex', flexDirection: 'column' }}
                        >
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
                                <FormControl size="small" sx={{ minWidth: 220 }}>
                                    <InputLabel>Heatmap Field</InputLabel>
                                    <Select
                                        value={selectedMapMetric}
                                        label="Heatmap Field"
                                        onChange={e => push(mapMetricStatePath, e.target.value)}
                                    >
                                        {mapMetricOptions.map(option => (
                                            <MenuItem key={option} value={option}>
                                                {option}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>
                            {googleMapsApiKey === '' && (
                                <Box sx={{ p: 2 }}>
                                    <Typography color="text.secondary">
                                        Google Maps API key is missing.
                                    </Typography>
                                </Box>
                            )}
                            {googleMapsApiKey !== '' && mapLoadError && (
                                <Box sx={{ p: 2 }}>
                                    <Typography color="error.main">
                                        Failed to load Google Maps.
                                    </Typography>
                                </Box>
                            )}
                            {googleMapsApiKey !== '' && !mapLoadError && !isMapLoaded && (
                                <Box sx={{ p: 2 }}>
                                    <Typography color="text.secondary">Loading map...</Typography>
                                </Box>
                            )}
                            {googleMapsApiKey !== '' && !mapLoadError && isMapLoaded && (
                                <>
                                    {(heatMapData as any).length === 0 ? (
                                        <Box
                                            sx={{
                                                flex: 1,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <Typography color="text.secondary">
                                                No map data found for the selected field.
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <GoogleMap
                                            onLoad={onMapLoad}
                                            onUnmount={onMapUnmount}
                                            mapContainerStyle={{
                                                width: '100%',
                                                height: '100%',
                                                borderRadius: 4,
                                            }}
                                            center={mapCenter}
                                            zoom={mapPoints.length > 0 ? 5 : 4}
                                            options={ReportingMapOptions}
                                        >
                                            <DeckHeatmapLayer data={heatMapData} />
                                        </GoogleMap>
                                    )}
                                </>
                            )}
                        </Box>
                    ) : (
                        <TableContainer sx={{ height: '100%', overflow: 'auto' }}>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        {visibleHeaders.map(header => (
                                            <TableCell
                                                key={header}
                                                sortDirection={
                                                    tableSort?.key === header
                                                        ? tableSort.dir
                                                        : false
                                                }
                                            >
                                                <TableSortLabel
                                                    active={tableSort?.key === header}
                                                    direction={
                                                        tableSort?.key === header
                                                            ? tableSort.dir
                                                            : 'asc'
                                                    }
                                                    onClick={() => handleTableSort(header)}
                                                >
                                                    {getDisplayHeaderLabel(header)}
                                                </TableSortLabel>
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {sortedGroupedRows.map(row => (
                                        <TableRow
                                            key={visibleHeaders
                                                .map(header =>
                                                    header === DELIVERY_PERCENTAGE_HEADER
                                                        ? getDeliveryPercent(row).label
                                                        : row[header] ?? '-',
                                                )
                                                .join('|')}
                                            hover
                                        >
                                            {visibleHeaders.map(header => (
                                                <TableCell
                                                    key={header}
                                                    sx={
                                                        header === DELIVERY_PERCENTAGE_HEADER
                                                            ? {
                                                                  color: getDeliveryPercent(row)
                                                                      .color,
                                                              }
                                                            : undefined
                                                    }
                                                >
                                                    {header === DELIVERY_PERCENTAGE_HEADER
                                                        ? getDeliveryPercent(row).label
                                                        : header === CONVERSION_RATE_HEADER &&
                                                          dataset === 'conversions'
                                                        ? getTableCellValue(row, header)
                                                        : formatTableValue(
                                                              header,
                                                              getTableCellValue(row, header),
                                                          )}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))}
                                    {groupedRows.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={Math.max(visibleHeaders.length, 1)}>
                                                No data found for selected filters.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </CardContent>
            </Card>
        </>
    );
};

export default ConversionMeasurementDatasetPanel;
